export const HOUSEHOLD_TIME_ZONE = 'Europe/Madrid';
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const PERIOD_RE = /^(\d{4})-(\d{2})$/;
export function isIsoDate(value) {
    if (typeof value !== 'string')
        return false;
    const m = DATE_RE.exec(value);
    if (!m)
        return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo);
}
export function assertIsoDate(value, label = 'fecha') {
    if (!isIsoDate(value))
        throw new TypeError(`${label} inválida: ${String(value)}`);
    return value;
}
export function isPeriod(value) {
    if (typeof value !== 'string')
        return false;
    const m = PERIOD_RE.exec(value);
    if (!m)
        return false;
    const mo = Number(m[2]);
    return mo >= 1 && mo <= 12;
}
export function assertPeriod(value, label = 'período') {
    if (!isPeriod(value))
        throw new TypeError(`${label} inválido: ${String(value)}`);
    return value;
}
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
export function parts(date) {
    const m = DATE_RE.exec(date);
    if (!m)
        throw new TypeError(`fecha inválida: ${date}`);
    return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}
export function makeDate(year, month, day) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
export function makePeriod(year, month) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}
export function periodParts(period) {
    const m = PERIOD_RE.exec(period);
    if (!m)
        throw new TypeError(`período inválido: ${period}`);
    return { year: Number(m[1]), month: Number(m[2]) };
}
export function periodOf(date) {
    return date.slice(0, 7);
}
export function firstDayOfPeriod(period) {
    return `${period}-01`;
}
export function lastDayOfPeriod(period) {
    const { year, month } = periodParts(period);
    return makeDate(year, month, daysInMonth(year, month));
}
export function addMonthsToPeriod(period, months) {
    const { year, month } = periodParts(period);
    const index = year * 12 + (month - 1) + months;
    return makePeriod(Math.floor(index / 12), (index % 12) + 1);
}
export function monthsBetween(from, to) {
    const a = periodParts(from);
    const b = periodParts(to);
    return (b.year - a.year) * 12 + (b.month - a.month);
}
export function periodRange(from, to) {
    const result = [];
    const n = monthsBetween(from, to);
    for (let i = 0; i <= n; i++)
        result.push(addMonthsToPeriod(from, i));
    return result;
}
function toEpochDay(date) {
    const { year, month, day } = parts(date);
    return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}
function fromEpochDay(days) {
    const d = new Date(days * 86_400_000);
    return makeDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
export function addDays(date, days) {
    return fromEpochDay(toEpochDay(date) + days);
}
export function daysBetween(from, to) {
    return toEpochDay(to) - toEpochDay(from);
}
/** Añade meses manteniendo el día cuando existe; si no, el último día del mes. */
export function addMonths(date, months, preferredDay) {
    const { year, month, day } = parts(date);
    const target = addMonthsToPeriod(makePeriod(year, month), months);
    const { year: ty, month: tm } = periodParts(target);
    const wanted = preferredDay ?? day;
    return makeDate(ty, tm, Math.min(wanted, daysInMonth(ty, tm)));
}
/** Día de la semana ISO: 1 = lunes … 7 = domingo. */
export function isoWeekday(date) {
    const { year, month, day } = parts(date);
    const js = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return js === 0 ? 7 : js;
}
export function compareDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
export function maxDate(a, b) {
    return a >= b ? a : b;
}
export function minDate(a, b) {
    return a <= b ? a : b;
}
/** Fecha civil y hora local (HH:MM) de un instante en la zona del hogar. */
export function localDateInZone(instant, timeZone = HOUSEHOLD_TIME_ZONE) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const partsList = fmt.formatToParts(instant);
    const get = (type) => partsList.find((p) => p.type === type)?.value ?? '00';
    return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
}
export const MONTH_NAMES_ES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
export const MONTH_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const WEEKDAY_NAMES_ES = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
export function periodLabel(period, style = 'long') {
    const { year, month } = periodParts(period);
    const name = style === 'long' ? MONTH_NAMES_ES[month - 1] : MONTH_SHORT_ES[month - 1];
    return style === 'long' ? `${name} ${year}` : `${name} ${String(year).slice(2)}`;
}
export function dateLabel(date) {
    const { year, month, day } = parts(date);
    return `${day} de ${MONTH_NAMES_ES[month - 1]} de ${year}`;
}
export function shortDateLabel(date) {
    const { month, day } = parts(date);
    return `${day} ${MONTH_SHORT_ES[month - 1]?.toLowerCase()}`;
}
//# sourceMappingURL=dates.js.map